import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionLabel } from "@/components/site/bits";
import { Input } from "@/components/ui/input";
import { Mic, MessageSquare, Gauge, Send, Bot, Globe, Play, Square } from "lucide-react";

const TABS = [
  { id: "voice", label: "AI Voice Agent", icon: Mic },
  { id: "chat", label: "AI Chatbot", icon: MessageSquare },
  { id: "audit", label: "Website Audit", icon: Globe },
  { id: "score", label: "Automation Score", icon: Gauge },
];

export default function LiveDemo() {
  const [tab, setTab] = useState("voice");
  return (
    <section id="live-demo" className="relative py-28 px-6" data-testid="live-demo">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <SectionLabel>Live Demo Zone</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            Don't just read about AI — <span className="text-gradient">experience it</span>
          </h2>
          <p className="mt-4 text-white/50 text-sm">Interactive product demos. (Illustrative previews of our capabilities.)</p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`demo-tab-${t.id}`}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors ${tab === t.id ? "bg-white text-[#0B1220] font-semibold" : "glass text-white/70 hover:text-white"}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8 rounded-3xl glass p-6 md:p-10 min-h-[380px]" data-testid="demo-panel">
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}>
              {tab === "voice" && <VoiceDemo />}
              {tab === "chat" && <ChatDemo />}
              {tab === "audit" && <AuditDemo />}
              {tab === "score" && <ScoreDemo />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function VoiceDemo() {
  const [active, setActive] = useState(false);
  const [caption, setCaption] = useState("Press call to start a simulated AI voice conversation.");
  const lines = [
    "Hi! This is Nova from 9nocAI. How can I help your business today?",
    "Great — I can help automate your lead follow-ups and bookings.",
    "I've scheduled a free consultation for you. You'll get a confirmation shortly!",
  ];
  useEffect(() => {
    if (!active) return;
    let i = 0;
    setCaption(lines[0]);
    const t = setInterval(() => { i++; if (i < lines.length) setCaption(lines[i]); else { setActive(false); clearInterval(t); } }, 2200);
    return () => clearInterval(t);
  }, [active]); // eslint-disable-line

  return (
    <div className="grid md:grid-cols-2 gap-8 items-center">
      <div className="flex flex-col items-center justify-center py-6">
        <div className="relative grid place-items-center">
          {active && [0, 1, 2].map((r) => (
            <motion.span key={r} className="absolute rounded-full border border-[#00E5FF]/40"
              initial={{ width: 90, height: 90, opacity: 0.6 }} animate={{ width: 220, height: 220, opacity: 0 }}
              transition={{ duration: 2, repeat: Infinity, delay: r * 0.6 }} />
          ))}
          <div className={`grid h-24 w-24 place-items-center rounded-full ${active ? "bg-gradient-to-br from-[#00E5FF] to-[#7B61FF]" : "glass"}`}>
            <Bot className="h-10 w-10 text-white" />
          </div>
        </div>
        <button onClick={() => setActive((v) => !v)} data-testid="demo-voice-btn"
          className={`mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors ${active ? "bg-red-500 text-white" : "bg-[#00FFB2] text-[#0B1220]"}`}>
          {active ? <><Square className="h-4 w-4" /> End Call</> : <><Play className="h-4 w-4" /> Start Voice Demo</>}
        </button>
      </div>
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 min-h-[160px]">
        <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Live Transcript</div>
        <p className="font-display text-lg leading-relaxed text-white/90" data-testid="demo-voice-caption">{caption}</p>
        {active && <div className="mt-4 flex gap-1 items-end h-8">{[...Array(20)].map((_, i) => (
          <motion.span key={i} className="flex-1 bg-[#00E5FF]/70 rounded" animate={{ height: [6, 24, 6] }} transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.05 }} />
        ))}</div>}
      </div>
    </div>
  );
}

function ChatDemo() {
  const [msgs, setMsgs] = useState([{ from: "bot", text: "Hi 👋 I'm the 9nocAI assistant. Ask me anything about our services!" }]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const REPLIES = {
    price: "Our projects start affordably and scale with scope. Book a free consultation for a tailored quote!",
    voice: "Our AI voice agents handle calls 24/7, qualify leads and book meetings automatically.",
    website: "We build fast, SEO-first, conversion-focused websites — usually live within 2–4 weeks.",
    default: "Great question! Our team can walk you through that on a free consultation call.",
  };
  const send = () => {
    if (!input.trim()) return;
    const q = input.toLowerCase();
    const key = q.includes("price") || q.includes("cost") ? "price" : q.includes("voice") || q.includes("call") ? "voice" : q.includes("web") ? "website" : "default";
    setMsgs((m) => [...m, { from: "user", text: input }]);
    setInput("");
    setTimeout(() => setMsgs((m) => [...m, { from: "bot", text: REPLIES[key] }]), 700);
  };

  return (
    <div className="mx-auto max-w-xl">
      <div className="h-64 overflow-y-auto space-y-3 pr-2" data-testid="demo-chat-window">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.from === "user" ? "bg-[#00E5FF] text-[#0B1220]" : "glass text-white/85"}`}>{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="mt-4 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Try: how much does a website cost?" data-testid="demo-chat-input" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
        <button onClick={send} data-testid="demo-chat-send" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#00E5FF] text-[#0B1220]"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function AuditDemo() {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = () => {
    if (!url) return;
    setLoading(true); setResult(null);
    setTimeout(() => {
      setResult({ perf: 62 + (url.length % 30), seo: 55 + (url.length % 40), a11y: 70 + (url.length % 25), best: 68 + (url.length % 28) });
      setLoading(false);
    }, 1400);
  };
  return (
    <div className="mx-auto max-w-2xl">
      <div className="flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yourwebsite.com" data-testid="demo-audit-input" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
        <button onClick={run} data-testid="demo-audit-btn" className="rounded-full bg-[#00FFB2] text-[#0B1220] px-6 font-semibold">Audit</button>
      </div>
      {loading && <div className="mt-8 text-center text-white/50 animate-pulse" data-testid="demo-audit-loading">Scanning your website...</div>}
      {result && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="demo-audit-result">
          {[["Performance", result.perf], ["SEO", result.seo], ["Accessibility", result.a11y], ["Best Practices", result.best]].map(([k, v]) => (
            <div key={k} className="rounded-2xl glass p-5 text-center">
              <div className={`font-display text-3xl font-bold ${v >= 80 ? "text-[#00FFB2]" : v >= 60 ? "text-[#00E5FF]" : "text-orange-400"}`}>{v}</div>
              <div className="text-xs text-white/50 mt-1">{k}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreDemo() {
  const opts = ["CRM in place", "Automated follow-ups", "AI support", "Analytics dashboards", "Voice/chat agents"];
  const [sel, setSel] = useState([]);
  const score = Math.round((sel.length / opts.length) * 100);
  const toggle = (o) => setSel((s) => s.includes(o) ? s.filter((x) => x !== o) : [...s, o]);
  return (
    <div className="grid md:grid-cols-2 gap-8 items-center">
      <div className="space-y-3">
        {opts.map((o) => (
          <button key={o} onClick={() => toggle(o)} data-testid={`demo-score-${o.replace(/\s+/g, "-").toLowerCase()}`}
            className={`w-full text-left rounded-2xl border p-4 text-sm transition-colors ${sel.includes(o) ? "border-[#00FFB2]/50 bg-[#00FFB2]/10 text-white" : "border-white/10 bg-white/[0.03] text-white/70"}`}>
            {o}
          </button>
        ))}
      </div>
      <div className="text-center">
        <div className="font-display text-6xl font-bold text-gradient" data-testid="demo-score-value">{score}</div>
        <div className="text-white/50 mt-1">Automation Score</div>
        <p className="mt-4 text-sm text-white/60 max-w-xs mx-auto">{score >= 80 ? "You're an automation leader! Let's push further." : score >= 40 ? "Solid start — big gains still ahead." : "Huge automation upside. Let's talk."}</p>
      </div>
    </div>
  );
}
